import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  fetchModuleUsageList,
  fetchModuleUsageStats,
  fetchModuleUsageFilterOptions,
} from "../../../../services/adminMonitorService";
import ModuleUsageFilters from "./ModuleUsageFilters";
import ModuleUsageDetailModal from "./ModuleUsageDetailModal";
import { ENTITY_LABELS } from "../../../../utils/labelMapping";

/* ─────────── constants ─────────── */

const ORCHESTRATOR_BASE_URL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;

const STATUS_COLORS = {
  RUNNING:    { bg: "#e3f2fd", color: "#1565c0", border: "#90caf9" },
  PENDING:    { bg: "#fff3e0", color: "#e65100", border: "#ffb74d" },
  INITIATED:  { bg: "#fff3e0", color: "#e65100", border: "#ffb74d" },
  RETRY:      { bg: "#f3e5f5", color: "#6a1b9a", border: "#ce93d8" },
  COMPLETED:  { bg: "#e8f5e9", color: "#1b5e20", border: "#a5d6a7" },
  FAILED:     { bg: "#ffebee", color: "#b71c1c", border: "#ef9a9a" },
  INACTIVE:   { bg: "#f5f5f5", color: "#424242", border: "#bdbdbd" },
  REPLICATED: { bg: "#e1f5fe", color: "#01579b", border: "#81d4fa" },
};

const MODULE_LABEL = {
  discovery:        "Discovery",
  get brandAudit() { return `Brand ${ENTITY_LABELS.audit}`; },
  categoryAnalysis: "Category Analysis",
};

const MODULE_ICON = {
  discovery:        "🔍",
  brandAudit:       "📊",
  categoryAnalysis: "📂",
};

const ACTIVE_STATUSES = ["RUNNING", "PENDING", "INITIATED", "RETRY"];

const DEFAULT_FILTERS = {
  from_date: "", to_date: "",
  customer_id: [], user_id: [], request_type: [], status: [],
  page: 1, limit: 20,
};

/* ─────────── helpers ─────────── */

function fmt(n) { return (n || 0).toLocaleString(); }

function formatDuration(start, end) {
  if (!start) return "-";
  const diff = Math.round((new Date(end || Date.now()) - new Date(start)) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

function formatDate(dt) {
  if (!dt) return "-";
  return new Date(dt).toLocaleString();
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/* ─────────── sub-components ─────────── */

function StatusChip({ status, small }) {
  const sc = STATUS_COLORS[status] || STATUS_COLORS.INACTIVE;
  const isActive = ACTIVE_STATUSES.includes(status);
  return (
    <span style={{
      background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
      borderRadius: 10, padding: small ? "1px 7px" : "2px 9px",
      fontSize: small ? 10 : 11, fontWeight: 500, whiteSpace: "nowrap",
      display: "inline-flex", alignItems: "center", gap: 3,
    }}>
      {isActive && (
        <span style={{
          width: 5, height: 5, borderRadius: "50%", background: sc.color,
          animation: "blink 1.2s infinite", display: "inline-block",
        }} />
      )}
      {status}
    </span>
  );
}

/* ─── Overview section ─── */
function OverviewSection({ stats, onRefresh, refreshing }) {
  if (!stats) return null;

  const { by_status = {}, by_module_status = {}, by_module = {}, active_now = 0, fetched_at } = stats;

  const failed  = by_status.FAILED || 0;
  const completed = by_status.COMPLETED || 0;
  const pending = (by_status.PENDING || 0) + (by_status.INITIATED || 0);
  const retry   = by_status.RETRY || 0;
  const total   = Object.values(by_module).reduce((a, b) => a + b, 0);

  const topCards = [
    { label: "Active Now",  value: active_now,  color: "#1565c0", bg: "#e3f2fd", border: "#90caf9" },
    { label: "Completed",   value: completed,   color: "#1b5e20", bg: "#e8f5e9", border: "#a5d6a7" },
    { label: "Failed",      value: failed,      color: "#b71c1c", bg: "#ffebee", border: "#ef9a9a" },
    { label: "Pending",     value: pending,     color: "#e65100", bg: "#fff3e0", border: "#ffb74d" },
    { label: "Retry",       value: retry,       color: "#6a1b9a", bg: "#f3e5f5", border: "#ce93d8" },
    { label: "Total",       value: total,       color: "#333",    bg: "#f5f5f5", border: "#e0e0e0" },
  ];

  const modules = Object.keys(MODULE_LABEL);

  return (
    <div style={{
      background: "#fff", border: "1px solid #e8eaf0",
      borderRadius: 10, padding: "18px 22px", marginBottom: 20,
      boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#222", letterSpacing: 0.3 }}>
            DATABASE OVERVIEW
          </span>
          <span style={{
            fontSize: 10, background: "#e8f0fe", color: "#1a56db",
            borderRadius: 4, padding: "2px 7px", fontWeight: 500,
          }}>
            LIVE
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {fetched_at && (
            <span style={{ fontSize: 11, color: "#999" }}>
              Last updated: {formatTime(fetched_at)}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={refreshing}
            style={{
              fontSize: 11, padding: "4px 11px", border: "1px solid #e0e0e0",
              borderRadius: 4, background: refreshing ? "#f5f5f5" : "#fff",
              cursor: refreshing ? "default" : "pointer", color: "#555",
              display: "flex", alignItems: "center", gap: 4,
            }}
          >
            <span style={{ display: "inline-block", animation: refreshing ? "spin 1s linear infinite" : "none" }}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Status cards */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {topCards.map((c) => (
          <div key={c.label} style={{
            background: c.bg, border: `1px solid ${c.border}`,
            borderRadius: 8, padding: "10px 16px", minWidth: 100,
            flex: "1 1 100px",
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1.2 }}>
              {fmt(c.value)}
            </div>
            <div style={{ fontSize: 11, color: c.color, opacity: 0.75, marginTop: 2, fontWeight: 500 }}>
              {c.label}
            </div>
          </div>
        ))}
      </div>

      {/* Module breakdown */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 10 }}>
          Module Breakdown
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {modules.map((mod) => {
            const ms = by_module_status[mod] || {};
            const modTotal = by_module[mod] || 0;
            const modActive = ACTIVE_STATUSES.reduce((s, st) => s + (ms[st] || 0), 0);
            const modCompleted = ms.COMPLETED || 0;
            const modFailed = ms.FAILED || 0;
            const pct = modTotal > 0 ? Math.round((modCompleted / modTotal) * 100) : 0;

            return (
              <div key={mod} style={{
                background: "#fafafa", border: "1px solid #e8eaf0",
                borderRadius: 8, padding: "12px 14px",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                    {MODULE_ICON[mod]} {MODULE_LABEL[mod]}
                  </span>
                  <span style={{ fontSize: 11, color: "#888", fontWeight: 500 }}>{fmt(modTotal)} total</span>
                </div>

                <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                  <StatPill label="Active"    value={modActive}    color="#1565c0" bg="#e3f2fd" />
                  <StatPill label="Completed" value={modCompleted} color="#1b5e20" bg="#e8f5e9" />
                  <StatPill label="Failed"    value={modFailed}    color="#b71c1c" bg="#ffebee" />
                  {ms.RETRY > 0 && <StatPill label="Retry" value={ms.RETRY} color="#6a1b9a" bg="#f3e5f5" />}
                </div>

                {/* Progress bar */}
                <div style={{ background: "#e0e0e0", borderRadius: 4, height: 5 }}>
                  <div style={{
                    background: "#2e7d32", width: `${pct}%`,
                    height: "100%", borderRadius: 4, transition: "width 0.4s",
                  }} />
                </div>
                <div style={{ fontSize: 10, color: "#999", marginTop: 3 }}>{pct}% completed</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, color, bg }) {
  return (
    <span style={{
      background: bg, color, borderRadius: 6,
      padding: "2px 8px", fontSize: 11, fontWeight: 500,
      display: "inline-flex", alignItems: "center", gap: 4,
    }}>
      <strong>{fmt(value)}</strong> {label}
    </span>
  );
}

/* ─────────── main component ─────────── */

export default function ModuleUsage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [listLoading, setListLoading] = useState(false);
  const [statsRefreshing, setStatsRefreshing] = useState(false);
  const [detailTxId, setDetailTxId] = useState(null);
  const [retryingIds, setRetryingIds] = useState(new Set());
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Stats: always global, no filters
  const loadStats = useCallback(async (showSpinner = false) => {
    if (showSpinner) setStatsRefreshing(true);
    try {
      const res = await fetchModuleUsageStats();
      setStats(res);
    } catch (err) {
      console.error(err);
    } finally {
      if (showSpinner) setStatsRefreshing(false);
    }
  }, []);

  // List: respects filters
  const loadList = useCallback(async (f) => {
    setListLoading(true);
    try {
      const res = await fetchModuleUsageList(f);
      setData(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  }, []);

  // On mount: load everything once
  useEffect(() => {
    loadStats(false);
    fetchModuleUsageFilterOptions().then(setFilterOptions).catch(console.error);
  }, [loadStats]);

  // Reload list whenever filters change
  useEffect(() => {
    loadList(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);


  async function handleRetry(row) {
    if (!ORCHESTRATOR_BASE_URL) {
      toast.error("Orchestrator URL not configured");
      return;
    }
    setRetryingIds((prev) => new Set([...prev, row.transaction_id]));
    try {
      await fetch(`${ORCHESTRATOR_BASE_URL}/retry-transaction-request/${row.transaction_id}`);
      toast.success(`Retry triggered for #${row.transaction_id}`);
      setTimeout(() => loadList(filtersRef.current), 2000);
    } catch {
      toast.error("Retry failed. Please try again.");
    } finally {
      setRetryingIds((prev) => { const n = new Set(prev); n.delete(row.transaction_id); return n; });
    }
  }

  const totalPages = Math.ceil(total / filters.limit);

  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── SECTION 1: Global Overview (filter-independent) ── */}
      <OverviewSection
        stats={stats}
        onRefresh={() => { loadStats(true); loadList(filtersRef.current); }}
        refreshing={statsRefreshing}
      />

      {/* ── SECTION 2: Transaction Records (filterable) ── */}
      <div style={{
        background: "#fff", border: "1px solid #e8eaf0",
        borderRadius: 10, padding: "18px 22px",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#222", letterSpacing: 0.3 }}>
            TRANSACTION RECORDS
          </span>
          <span style={{ fontSize: 11, color: "#999" }}>
            — filter and explore individual transactions
          </span>
        </div>

        <ModuleUsageFilters
          filters={filters}
          onChange={setFilters}
          filterOptions={filterOptions}
        />

        {/* Table controls */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#666" }}>
            {listLoading ? "Loading…" : `${total.toLocaleString()} transaction${total !== 1 ? "s" : ""}`}
          </span>
          <select
            value={filters.limit}
            onChange={(e) => setFilters((f) => ({ ...f, limit: parseInt(e.target.value), page: 1 }))}
            style={{ fontSize: 12, padding: "3px 6px", border: "1px solid #ccc", borderRadius: 4 }}
          >
            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: "#f8f8f8" }}>
                {["Tx ID", "Module", "Brand", "Customer", "User", "Status", "Started", "Duration", "Requests", "Failed", "Actions"].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!listLoading && data.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: "center", padding: 36, color: "#bbb", fontSize: 13 }}>
                    No transactions match the current filters
                  </td>
                </tr>
              )}
              {data.map((row) => {
                const isFailed = row.status === "FAILED" || row.status === "RETRY";
                const isRetrying = retryingIds.has(row.transaction_id);
                return (
                  <tr key={row.transaction_id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={tdStyle}>
                      <button
                        onClick={() => setDetailTxId(row.transaction_id)}
                        style={{ background: "none", border: "none", color: "#1976d2", cursor: "pointer", fontSize: 12, padding: 0, textDecoration: "underline" }}
                      >
                        #{row.transaction_id}
                      </button>
                    </td>
                    <td style={tdStyle}><span style={{ fontSize: 12 }}>{MODULE_LABEL[row.request_type] || row.request_type}</span></td>
                    <td style={{ ...tdStyle, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span title={row.brand_domain} style={{ fontSize: 12 }}>{row.brand_domain || "-"}</span>
                    </td>
                    <td style={tdStyle}><span style={{ fontSize: 12 }}>{row.customer_name || "-"}</span></td>
                    <td style={{ ...tdStyle, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span title={row.user_email} style={{ fontSize: 12 }}>{row.user_email || "-"}</span>
                    </td>
                    <td style={tdStyle}><StatusChip status={row.status} /></td>
                    <td style={tdStyle}><span style={{ fontSize: 11, whiteSpace: "nowrap" }}>{formatDate(row.start_time)}</span></td>
                    <td style={tdStyle}><span style={{ fontSize: 12 }}>{formatDuration(row.start_time, row.end_time)}</span></td>
                    <td style={{ ...tdStyle, textAlign: "center" }}><span style={{ fontSize: 12 }}>{row.completed_requests}/{row.total_requests}</span></td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {row.failed_requests > 0
                        ? <span style={{ fontSize: 12, color: "#d32f2f", fontWeight: 600 }}>{row.failed_requests}</span>
                        : <span style={{ fontSize: 12, color: "#bbb" }}>0</span>
                      }
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <button onClick={() => setDetailTxId(row.transaction_id)} style={viewBtnStyle}>View</button>
                      {isFailed && (
                        <button
                          onClick={() => handleRetry(row)}
                          disabled={isRetrying}
                          style={{ ...viewBtnStyle, background: "#fff3e0", color: "#e65100", border: "1px solid #ffb74d", marginLeft: 4 }}
                        >
                          {isRetrying ? "…" : "Retry"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", marginTop: 12, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#666", marginRight: 6 }}>Page {filters.page} of {totalPages}</span>
            <button onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))} disabled={filters.page <= 1} style={pageBtnStyle}>‹ Prev</button>
            <button onClick={() => setFilters((f) => ({ ...f, page: Math.min(totalPages, f.page + 1) }))} disabled={filters.page >= totalPages} style={pageBtnStyle}>Next ›</button>
          </div>
        )}
      </div>

      {detailTxId && (
        <ModuleUsageDetailModal transactionId={detailTxId} onClose={() => setDetailTxId(null)} />
      )}

      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes spin  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 12, background: "#fff", border: "1px solid #eeeeee", borderRadius: 6, overflow: "hidden" };
const thStyle    = { padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#555", borderBottom: "1px solid #eeeeee", whiteSpace: "nowrap" };
const tdStyle    = { padding: "8px 10px", verticalAlign: "middle" };
const viewBtnStyle = { padding: "3px 10px", background: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: 4, fontSize: 11, cursor: "pointer" };
const pageBtnStyle = { padding: "4px 10px", border: "1px solid #ccc", borderRadius: 4, background: "#fff", fontSize: 12, cursor: "pointer" };
